import { ComponentType } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { merge } from 'lodash';
import { ZuperDialogComponent } from './zuper-dialog.component';
import { ZuperDialogConfig } from './zuper-dialog.types';

@Injectable()
export class ZuperDialogService
{
    localizationValue: any = {};
    count: number = 0;
    private _defaultConfig: ZuperDialogConfig = {
        title      : 'Confirm action',
        message    : 'Are you sure you want to confirm this action?',
        icon       : {
            show : true,
            name : 'heroicons_outline:exclamation',
            color: 'primary'
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Confirm',
                color: 'primary',
            },
            cancel : {
                show : true,
                label: 'Cancel'
            }
        },
        size : '',
        width : '',
        height : '',
        extraClass: '',
        dismissible: false,
    };

    private _successConfig: ZuperDialogConfig = {
        title      : 'Success',
        icon       : {
            show : true,
            name : 'heroicons_outline:check',
            color: 'success'
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Ok',
                color: 'success',
                confirmDisplayKey: 'text.success',
            },
            cancel : {
                show : false,
                cancelDisplayKey: 'cancel.text',
                label: 'Cancel'
            }
        }
    };

    private _errorConfig: ZuperDialogConfig = {
        title      : 'Error',
        icon       : {
            show : true,
            name : 'heroicons_outline:exclamation',
            color: 'error'
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Ok',
                color: 'error',
            },
            cancel : {
                cancelDisplayKey: 'cancel.text',
                show : false,
                label: 'Cancel'
            }
        },
    };

    private _warnConfig: ZuperDialogConfig = {
        title      : 'Warning',
        icon       : {
            show : true,
            name : 'heroicons_outline:exclamation',
            color: 'warning'
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Ok',
                color: 'warning',
            },
            cancel : {
                show : false,
                label: 'Cancel',
                cancelDisplayKey: 'cancel.text',
            }
        }
    };

    private _infoConfig: ZuperDialogConfig = {
        title      : 'Info',
        icon       : {
            show : true,
            name : 'heroicons_outline:information-circle',
            color: 'info'
        },
        actions    : {
            confirm: {
                show : true,
                label: 'Ok',
                color: 'info',
            },
            cancel : {
                show : false,
                label: 'Cancel',
                cancelDisplayKey: 'cancel.text',
            }
        }
    };

    /**
     * Constructor
     */
    constructor(
        private _matDialog: MatDialog,
        private http: HttpClient,
    )
    {}

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    open(config: ZuperDialogConfig = {}): MatDialogRef<ZuperDialogComponent>
    {
        // Merge the user config with the default config
        let userConfig = {...this._defaultConfig};
        switch(config.type){
            case 'success':
                userConfig = {...userConfig,...this._successConfig};
                break;
            case 'error':
                userConfig = {...userConfig,...this._errorConfig};
                break;
            case 'warning':
                userConfig = {...userConfig,...this._warnConfig};
                break;
            case 'info':
                userConfig = {...userConfig,...this._infoConfig};
                break;
            }
        userConfig = {...userConfig,...config};

        // Open the dialog
        return this._matDialog.open(ZuperDialogComponent, {
            autoFocus   : false,
            disableClose: !userConfig.dismissible,
            data        : userConfig,
            panelClass  : userConfig.extraClass,
            hasBackdrop: true,
            closeOnNavigation: true
        });
    }

    openCustom<T, D = any, R = any>(component: ComponentType<T>, config?: any): MatDialogRef<T, R>
    {
        // Merge the user config with the default config
        const userConfig = merge({}, this._defaultConfig, config);

        // Open the dialog
        return this._matDialog.open(component, {
            autoFocus   : false,
            disableClose: !userConfig.dismissible,
            data        : userConfig,
            panelClass  : userConfig.extraClass,
            hasBackdrop: true,
            closeOnNavigation: true
        });
    }
}
